import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";

import CoinDrawerSelect from "components/CoinDrawerSelect";
import { Grid } from "@mui/material";
import RepayAmount from "./Components/RepayAmount";
import RepayInfo from "./Components/RepayInfo";
import RepaySubmit from "./Components/RepaySubmit";
import { borrowOrRepaySchema } from "utils/validations/borrowOrRepaySchema";
import { repeat } from "utils/helpers/interval";
import useNotify from "hooks/useNotify";
import { useSearchParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import useDispatch from "hooks/useDispatch";
import { useSelector } from "react-redux";
import { getRepayment, getRepaymentAmountMax } from "store/slices/repay/repaySlice";

let getRepayAmountMaxInterval;

function Repay({ onClose }) {
  const dispatch = useDispatch()
  const notify = useNotify();

  const repayInitial = useSelector((state) => state.repay.repayInitial?.data)
  const repayAmountMax = useSelector((state) => state.repay.repayAmountMax?.data)

  const [loading, setLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const coinId = +searchParams.get("repay") || 1;
  const basketId = +searchParams.get("basketId");

  const methods = useForm({
    defaultValues: {
      coinId,
      amount: "",
      basketOwnerId: basketId
    },
    resolver: yupResolver(borrowOrRepaySchema()),
  });

  const { reset } = methods;

  const handleGetRepayAmountMax = async ({ coinId, basketOwnerId: basketId }) => {
    try {
      setLoading(true);
      dispatch(getRepaymentAmountMax({ coinId, basketOwnerId: basketId }));

    } catch (e) {
      notify(e?.response?.data?.message, "error");
    }
    setLoading(false);
  };

  const selectedCoinIsInLisCoin = repayInitial?.lisCoin?.find(({ id }) => id === coinId)

  useEffect(() => {
    if (repayInitial?.validSeconds && coinId && selectedCoinIsInLisCoin) {
      handleGetRepayAmountMax({ coinId, basketOwnerId: basketId });

      clearInterval(getRepayAmountMaxInterval);

      getRepayAmountMaxInterval = repeat({
        action: () => handleGetRepayAmountMax({ coinId, basketOwnerId: basketId }),
        time: repayInitial?.validSeconds * 1000,
      });
    }

    return () => {
      clearInterval(getRepayAmountMaxInterval);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coinId, repayInitial, selectedCoinIsInLisCoin]);

  const handleChangeCoin = (data) => {
    const allSearchParams = Object.fromEntries([...searchParams]);
    setSearchParams({ ...allSearchParams, repay: data?.id });
  };

  const selectedCoin = useMemo(
    () => {
      const allSearchParams = Object.fromEntries([...searchParams]);
      const selected = repayInitial?.lisCoin?.find((coin) => coin?.id === coinId)

      if (!selected) {
        const firstOption = repayInitial?.lisCoin?.[0]

        setSearchParams({ ...allSearchParams, repay: firstOption?.id });
      }

      return selected
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [repayInitial, coinId]
  );

  useEffect(() => {
    dispatch(getRepayment({ coinId, basketOwnerId: basketId }));


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reset((formValues) => ({
      ...formValues,
      amount: "",
      coinId,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coinId, reset]);

  return (
    <FormProvider {...methods}>
      <form>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CoinDrawerSelect
              coinList={repayInitial}
              onCoinClick={handleChangeCoin}
              selectedCoin={selectedCoin}
            />
          </Grid>

          <RepayAmount
            loading={loading}
            repayAmountMax={repayAmountMax}
            selectedCoin={selectedCoin}
          />

          <RepayInfo
            repayAmountMax={repayAmountMax}
            selectedCoin={selectedCoin}
          />

          <RepaySubmit
            onClose={onClose}
            repayAmountMax={repayAmountMax}
            selectedCoin={selectedCoin}
          />
        </Grid>
      </form>
    </FormProvider>
  );
}

export default Repay;
